import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { Plus, Trash2, BookOpen, ChevronDown, ChevronUp, Save, Sparkles, Loader2, FileText, PenLine, Info, FileDown } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const STATUS_MAP = {
  idea: { label: 'Idea', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  draft: { label: 'Draft', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  revision: { label: 'Revision', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  final: { label: 'Final', color: 'bg-green-50 text-green-700 border-green-200' },
};

const ESSAY_TYPES = {
  personal_statement: { label: 'Personal Statement', icon: PenLine, color: 'text-[#0F392B]' },
  supplemental: { label: 'Supplemental', icon: FileText, color: 'text-blue-600' },
  additional_info: { label: 'Additional Info', icon: Info, color: 'text-amber-600' },
};

export default function EssayStudio() {
  const [essays, setEssays] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiFeedback, setAiFeedback] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(null);
  const [view, setView] = useState('all');
  const [form, setForm] = useState({
    title: '', essay_type: 'personal_statement', school: '',
    prompt: '', prompt_number: null, word_limit: 650, status: 'idea',
  });

  const load = useCallback(() => {
    Promise.all([
      api.getEssays(),
      api.getEssayPrompts().catch(() => ({ prompts: [] })),
    ]).then(([e, p]) => {
      setEssays(e);
      setPrompts(p.prompts || []);
    }).catch(() => toast.error('Failed to load essays'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.createEssay({
        ...form,
        word_limit: form.word_limit ? parseInt(form.word_limit) : null,
        prompt_number: form.prompt_number ? parseInt(form.prompt_number) : null,
      });
      toast.success('Essay created');
      setForm({ title: '', essay_type: 'personal_statement', school: '', prompt: '', prompt_number: null, word_limit: 650, status: 'idea' });
      setOpen(false);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await api.deleteEssay(id);
      toast.success('Essay deleted');
      if (expanded === id) setExpanded(null);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleExpand = (essay) => {
    if (expanded === essay.id) { setExpanded(null); }
    else { setExpanded(essay.id); setEditContent(essay.content || ''); setAiFeedback(null); }
  };

  const handleSave = async (id) => {
    setSaving(true);
    try {
      await api.updateEssay(id, { content: editContent });
      toast.success('Saved');
      load();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (id, status) => {
    try { await api.updateEssay(id, { status }); load(); } catch (err) { toast.error(err.message); }
  };

  const handlePdfExport = async (essayId, title) => {
    setPdfLoading(essayId);
    try {
      const blob = await api.downloadEssayPdf(essayId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/[^a-zA-Z0-9 _-]/g, '')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) { toast.error('PDF export failed'); }
    finally { setPdfLoading(null); }
  };

  const handleAIFeedback = async (essayId, feedbackType) => {
    if (!editContent.trim()) { toast.error('Write some content first'); return; }
    setAiLoading(true);
    setAiFeedback(null);
    try {
      const essay = essays.find(e => e.id === essayId);
      const result = await api.generateAIFeedback(essayId, {
        feedback_type: feedbackType,
        essay_content: editContent,
        prompt: essay?.prompt || '',
      });
      setAiFeedback(result);
      toast.success('AI feedback ready');
    } catch (err) { toast.error(err.message || 'AI feedback failed'); }
    finally { setAiLoading(false); }
  };

  const handlePromptSelect = (num) => {
    const p = prompts.find(pr => pr.number === num);
    if (p) {
      setForm(prev => ({
        ...prev,
        prompt: p.prompt,
        prompt_number: p.number,
        word_limit: p.word_limit,
        title: prev.title || `Personal Statement — Prompt ${p.number}`,
      }));
    }
  };

  const wordCount = (text) => (text || '').trim().split(/\s+/).filter(Boolean).length;

  const personalStatements = essays.filter(e => (e.essay_type || 'personal_statement') === 'personal_statement');
  const supplementals = essays.filter(e => e.essay_type === 'supplemental');
  const additionalInfo = essays.filter(e => e.essay_type === 'additional_info');

  const filtered = view === 'all' ? essays
    : view === 'personal_statement' ? personalStatements
    : view === 'supplemental' ? supplementals
    : additionalInfo;

  const totalWords = essays.reduce((s, e) => s + wordCount(e.content), 0);
  const finalCount = essays.filter(e => e.status === 'final').length;

  if (loading) return (
    <div className="max-w-3xl mx-auto space-y-3 animate-pulse">
      {[1,2,3].map(i => <div key={i} className="h-20 bg-[#E2E2E2] rounded-xl" />)}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="essay-studio-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Writing
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            {essays.length} essays · {totalWords.toLocaleString()} words · {finalCount} finalized
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white text-xs h-8" data-testid="add-essay-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> New Essay
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>New Essay</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Essay Type</Label>
                <Select value={form.essay_type} onValueChange={v => setForm({ ...form, essay_type: v })}>
                  <SelectTrigger data-testid="essay-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal_statement">Personal Statement (650 words)</SelectItem>
                    <SelectItem value="supplemental">Supplemental Essay</SelectItem>
                    <SelectItem value="additional_info">Additional Information (250 words)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.essay_type === 'personal_statement' && prompts.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Common App Prompt</Label>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto bg-[#F9F9F7] rounded-lg p-2">
                    {prompts.map(p => (
                      <button key={p.number} type="button"
                        onClick={() => handlePromptSelect(p.number)}
                        className={`w-full text-left p-2.5 rounded-md border text-xs transition-all ${form.prompt_number === p.number ? 'bg-white border-[#0F392B] text-[#0F392B]' : 'bg-white/50 border-transparent hover:border-[#E2E2E2]'}`}
                        data-testid={`prompt-${p.number}`}>
                        <span className="font-semibold">Prompt {p.number}:</span> {p.prompt.slice(0, 100)}...
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Title</Label>
                  <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                    placeholder="Essay title" required data-testid="essay-title-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">School (optional)</Label>
                  <Input value={form.school} onChange={e => setForm({ ...form, school: e.target.value })}
                    placeholder="e.g., Harvard" data-testid="essay-school-input" />
                </div>
              </div>

              {form.essay_type !== 'personal_statement' && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Prompt</Label>
                  <Textarea value={form.prompt} onChange={e => setForm({ ...form, prompt: e.target.value })}
                    placeholder="The essay prompt..." rows={2} data-testid="essay-prompt-input" />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Word Limit</Label>
                  <Input type="number" value={form.word_limit || ''} onChange={e => setForm({ ...form, word_limit: e.target.value })}
                    placeholder="650" data-testid="essay-word-limit-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Status</Label>
                  <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="idea">Idea</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button type="submit" className="w-full bg-[#0F392B] hover:bg-[#0F392B]/90 text-white" data-testid="essay-submit-btn">
                Create Essay
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-[#F2F2F0] rounded-full overflow-hidden">
        <div className="h-full bg-[#0F392B] rounded-full transition-all duration-500"
          style={{ width: `${essays.length > 0 ? (finalCount / essays.length) * 100 : 0}%` }} />
      </div>

      {/* View Tabs */}
      <div className="flex gap-1 bg-[#F9F9F7] p-1 rounded-lg" data-testid="essay-tabs">
        {[
          { id: 'all', label: `All (${essays.length})` },
          { id: 'personal_statement', label: `Personal (${personalStatements.length})` },
          { id: 'supplemental', label: `Supplemental (${supplementals.length})` },
          { id: 'additional_info', label: `Additional (${additionalInfo.length})` },
        ].map(t => (
          <button key={t.id} onClick={() => setView(t.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex-1 text-center
              ${view === t.id ? 'bg-white text-[#0F392B] shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Essay List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <BookOpen className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground text-sm">No essays yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            {view === 'personal_statement' ? 'Start your Common App personal statement (250-650 words)' : 'Create your first essay'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(essay => {
            const wc = wordCount(expanded === essay.id ? editContent : essay.content);
            const limit = essay.word_limit;
            const overLimit = limit && wc > limit;
            const typeInfo = ESSAY_TYPES[essay.essay_type] || ESSAY_TYPES.personal_statement;
            const TypeIcon = typeInfo.icon;

            return (
              <Card key={essay.id} className={`border transition-all ${expanded === essay.id ? 'border-[#0F392B]/30 shadow-sm' : 'border-[#E2E2E2]'}`}
                data-testid={`essay-card-${essay.id}`}>
                <CardContent className="p-0">
                  {/* Header row */}
                  <div className="flex items-center gap-3 px-5 py-3.5 cursor-pointer" onClick={() => handleExpand(essay)}
                    data-testid={`essay-expand-${essay.id}`}>
                    <TypeIcon className={`h-4 w-4 ${typeInfo.color} flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-medium truncate">{essay.title}</h3>
                        <Badge variant="outline" className={`text-[10px] ${STATUS_MAP[essay.status]?.color || ''}`}>
                          {STATUS_MAP[essay.status]?.label || essay.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {essay.school && <span className="text-xs text-muted-foreground">{essay.school}</span>}
                        <span className={`text-xs ${overLimit ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                          {wc} {limit ? `/ ${limit}` : ''} words
                        </span>
                        <span className="text-xs text-muted-foreground">v{essay.version_number}</span>
                      </div>
                    </div>
                    <button onClick={e => { e.stopPropagation(); handleDelete(essay.id, essay.title); }}
                      className="text-muted-foreground hover:text-red-500 p-1" data-testid={`delete-essay-${essay.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    {expanded === essay.id ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                  </div>

                  {/* Expanded Editor */}
                  {expanded === essay.id && (
                    <div className="border-t border-[#E2E2E2] p-5 space-y-4 bg-[#FAFAF8]">
                      {/* Prompt display */}
                      {essay.prompt && (
                        <div className="bg-white rounded-lg p-3 border border-[#E2E2E2]">
                          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                            {essay.prompt_number ? `Common App Prompt ${essay.prompt_number}` : 'Prompt'}
                          </p>
                          <p className="text-xs leading-relaxed">{essay.prompt}</p>
                        </div>
                      )}

                      {/* Word count bar */}
                      {limit && (
                        <div className="space-y-1">
                          <div className="flex justify-between text-[10px]">
                            <span className="text-muted-foreground">{limit === 650 ? '250 min' : ''}</span>
                            <span className={overLimit ? 'text-red-500 font-bold' : 'text-muted-foreground'}>{wc}/{limit} words</span>
                          </div>
                          <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${overLimit ? 'bg-red-500' : wc >= (limit === 650 ? 250 : 0) ? 'bg-[#0F392B]' : 'bg-amber-400'}`}
                              style={{ width: `${Math.min(100, (wc / limit) * 100)}%` }} />
                          </div>
                        </div>
                      )}

                      {/* Editor */}
                      <Textarea value={editContent} onChange={e => setEditContent(e.target.value)}
                        rows={14} className="bg-white text-sm leading-relaxed font-serif resize-none"
                        placeholder="Start writing your essay..." data-testid={`essay-editor-${essay.id}`} />

                      {/* Controls */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Select value={essay.status} onValueChange={v => handleStatusChange(essay.id, v)}>
                            <SelectTrigger className="h-7 w-28 text-xs" data-testid={`essay-status-${essay.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="idea">Idea</SelectItem>
                              <SelectItem value="draft">Draft</SelectItem>
                              <SelectItem value="revision">Revision</SelectItem>
                              <SelectItem value="final">Final</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" onClick={() => handlePdfExport(essay.id, essay.title)}
                            disabled={pdfLoading === essay.id}
                            className="text-xs h-8 border-[#E2E2E2]" data-testid={`export-essay-pdf-${essay.id}`}>
                            {pdfLoading === essay.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <FileDown className="h-3.5 w-3.5 mr-1" />}
                            PDF
                          </Button>
                          <Button size="sm" onClick={() => handleSave(essay.id)} disabled={saving}
                            className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white text-xs h-8" data-testid={`save-essay-${essay.id}`}>
                            {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                            Save
                          </Button>
                        </div>
                      </div>

                      {/* AI Feedback */}
                      <div className="bg-white rounded-lg border border-[#E2E2E2] p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                          <span className="text-xs font-semibold">AI Essay Coach</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {[
                            { type: 'brainstorming', label: 'Brainstorm' },
                            { type: 'improvement', label: 'Improve' },
                            { type: 'grammar', label: 'Grammar' },
                            { type: 'opening', label: 'Stronger Opening' },
                          ].map(opt => (
                            <Button key={opt.type} size="sm" variant="outline" disabled={aiLoading}
                              onClick={() => handleAIFeedback(essay.id, opt.type)}
                              className="text-[11px] h-7 border-[#E2E2E2]" data-testid={`ai-${opt.type}-${essay.id}`}>
                              {aiLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                              {opt.label}
                            </Button>
                          ))}
                        </div>
                        {aiFeedback && expanded === essay.id && (
                          <div className="bg-[#F9F9F7] rounded-lg p-4 border border-[#E2E2E2]" data-testid={`ai-feedback-result-${essay.id}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <Sparkles className="h-3 w-3 text-[#D4AF37]" />
                              <span className="text-[10px] font-semibold text-[#D4AF37] uppercase tracking-wide">
                                {aiFeedback.feedback_type} Feedback
                              </span>
                            </div>
                            <div className="text-xs leading-relaxed whitespace-pre-wrap">{aiFeedback.feedback_text}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <p className="text-xs text-muted-foreground text-center">
        Personal statements: 250-650 words. Supplementals vary by school. All changes auto-version.
      </p>
    </div>
  );
}
